import { useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { Popover } from "../primitives/Popover";
import { Spinner } from "../primitives/Spinner";
import { cx } from "../utils/format";
import type { Navigate } from "./AppShell";
import { SOURCE_ICON, type SourceRowData } from "./Knowledge";

/* M14 round five (the founder, 2026-10-11: "tables instead of card lists, with the kit's one table style"): the knowledge overview and a
 * scope's sources as rows of the kit's table (`ob-dtable` in its card). The card versions (`HealthCard`, `SourceRow`) stay beside these:
 * the prototype still shows them. Columns that matter less leave on narrower cards (the kit's container rules); the first column and the
 * row's action always stay. */

/* ---------- the overview: one row per scope ---------- */
export interface HealthRowData {
  id: string; initials: string; name: string; meta: string;
  /** where the row leads (the scope's articles, or its setup when it has no knowledge yet) */ href: string;
  /** no knowledge base yet: the numbers are dashes and the last cell offers "Set one up" (to `href`) */ empty?: boolean;
  /** M14 round six: single numbers */ articles?: number; sources?: number; cited?: number; sizePct?: number;
  /** things that are broken or overdue: a marker beside the name, its tooltip names the count and what (M14 round six) */ attention?: { count: number; what: string[] };
}
/** the overview's table: Project, Articles, Sources, Cited in drafts, Knowledge size, and the action column ("Set one up", or the chevron) */
export function HealthTable({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-health-table" aria-label={label}>
        <thead><tr>
          <th>Project</th><th className="ob-dtable__num">Articles</th><th className="ob-dtable__num ob-dtable__col--below-540">Sources</th>
          <th className="ob-dtable__num ob-dtable__col--below-820">Cited in drafts</th><th className="ob-dtable__col--below-680">Knowledge size</th><th></th>
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** the tooltip of a scope's marker: "1 thing needs attention: <what>" */
export const attentionTip = (a: { count: number; what: string[] }): string => `${a.count === 1 ? "1 thing needs attention" : `${a.count} things need attention`}${a.what.length ? `: ${a.what.join("; ")}` : ""}`;

/** One scope of the overview (M14 round six: the whole row leads into the scope; the name is its link for the keyboard): the name with
 *  the attention marker and the meta below, the three numbers, the size meter; a scope without knowledge reads dashes and offers "Set one up". */
export function HealthRow({ row: r, onNavigate }: { row: HealthRowData; onNavigate: Navigate }) {
  const dash = <span className="ob-dtable__muted">—</span>;
  const num = (n?: number) => (r.empty || n === undefined ? dash : n);
  const tip = r.attention?.count ? attentionTip(r.attention) : null;
  const open = (e: MouseEvent<HTMLTableRowElement>) => { if ((e.target as HTMLElement).closest("a, button, input, label")) return; onNavigate(r.href, e as unknown as MouseEvent<HTMLAnchorElement>); };
  return (
    <tr className="ob-dtable__row ob-health-row" aria-label={r.name} onClick={open}>
      <td className="ob-dtable__grow"><span className="ob-dtable__person"><span className="ob-choice__icon" aria-hidden="true">{r.initials}</span><span className="ob-dtable__who">
        <span className="ob-health-row__title"><a className="ob-dtable__name ob-health-row__name" href={r.href} onClick={(e) => onNavigate(r.href, e)} title={r.name}>{r.name}</a>
          {tip && <span className="ob-health-row__alert" role="img" aria-label={tip} data-tip={tip} tabIndex={0}><Icon name="error" size="xs" /></span>}</span>
        <span className="ob-dtable__email" title={r.meta}>{r.meta}</span></span></span></td>
      <td className="ob-dtable__num">{num(r.articles)}</td>
      <td className="ob-dtable__num ob-dtable__col--below-540">{num(r.sources)}</td>
      <td className="ob-dtable__num ob-dtable__col--below-820">{num(r.cited)}</td>
      <td className="ob-dtable__col--below-680">{r.empty || r.sizePct === undefined ? dash : <span className="ob-health-row__size"><span className="ob-size__bar ob-health-row__bar" role="meter" aria-label="Knowledge size" aria-valuenow={r.sizePct} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${r.sizePct}%` }} /></span>{r.sizePct} %</span>}</td>
      <td className="ob-dtable__actions">{r.empty
        ? <a className="ob-link" href={r.href} onClick={(e) => onNavigate(r.href, e)}>Set one up</a>
        : <span className="ob-health-row__open" aria-hidden="true"><Icon name="chevronRight" size="sm" /></span>}</td>
    </tr>
  );
}

/* ---------- a scope's sources: one row per source ---------- */
/** the sources' table: Source, Kind, Items, State, and the menu (no AI column since the cleanup round, 2026-10-12: every source feeds the
 *  AI; the founder, 2026-10-16: the last read's date moved into the State cell's tooltip, so its own column went) */
export function SourceTable({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-source-table" aria-label={label}>
        <thead><tr>
          <th>Source</th><th className="ob-dtable__col--below-820">Kind</th><th className="ob-dtable__num ob-dtable__col--below-540">Items</th>
          <th>State</th><th></th>
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
const SOURCE_COLUMNS = 5;

/** One source as a table row: its name with its kind's icon (a muted second line, the location; a repository its address alone), kind,
 *  items, the state, the menu. The founder, 2026-10-16: the Items number and the State words carry the kit's tooltip (`itemsTip`, `stateTip`:
 *  what the number counts; when it was last read and how often, or what the state means), focusable and read to a screen reader. M14 round six (the
 *  founder): a failure, or a failed try the worker retries, is an alert icon in the State cell ("Why it failed") that opens a popover with the
 *  sentence and the fix (`failActions`, and Retry sync); only an explanation (`note`, the tickets source's) keeps the sub-row right below.
 *  The founder, 2026-10-12: Retry sync lives only in that popover (a normal button), no longer in the State cell. The founder, 2026-10-16:
 *  while a new website is analysed (`analysing`), the Kind cell and the State cell show the kit's spinner (hidden from screen readers) beside
 *  the word, the State cell in place of its sync words (the Kind column leaves narrow cards); a failed source shows its failure instead, and
 *  while a failed try waits for the worker's retry the State cell keeps its sync words ("retrying") beside "Why it failed". The knowledge
 *  sources round (2026-10-22): a row's `line` (a website's read line) replaces the location under the name and wraps, so a phone sees it
 *  too; the Kind cell has no tooltip (the Sections dialog says what each section is). */
export function SourceTableRow({ row, onRetry, menu, note, failActions }: { row: SourceRowData; onRetry?: () => void; menu?: ReactNode; note?: ReactNode; failActions?: ReactNode }) {
  const failed = row.syncState === "failed";
  const analysing = !!row.analysing && !failed;
  const stateAnalysing = analysing && !row.error;
  return (
    <>
      <tr className={cx("ob-dtable__row--static ob-source", failed && "ob-source--failed")}>
        <td className="ob-source__cell"><span className="ob-dtable__person"><span className="ob-source__kind"><Icon name={SOURCE_ICON[row.kind]} size="sm" /></span><span className="ob-dtable__who">
          <SourceName row={row} />
          {row.line ? <span className="ob-dtable__email ob-source__line">{row.line}</span> : !row.address && <span className="ob-dtable__email" title={row.location}>{row.location}</span>}</span></span></td>
        <td className="ob-dtable__muted ob-dtable__col--below-820 ob-source__kindline">{analysing ? <Analysing label={row.kindLabel} /> : row.kindLabel}</td>
        <td className="ob-dtable__num ob-dtable__col--below-540"><Tip text={row.itemsTip}>{row.items}</Tip></td>
        <td className="ob-source__state">
          <span className={cx("ob-source__sync", (row.syncState === "syncing" || failed) && "ob-source__sync--strong")}>
            {row.syncState === "syncing" && !stateAnalysing && <span className="ob-source__bar" aria-hidden="true"><span style={{ width: `${Math.round((row.progress ?? 0) * 100)}%` }} /></span>}
            {(row.error || failed) && <WhyFailed name={row.name} error={row.error ?? "The last sync failed."} onRetry={failed ? onRetry : undefined} actions={failActions} />}<Tip text={row.stateTip}>{stateAnalysing ? <Analysing label="Analysing" /> : row.syncLabel}</Tip>
          </span>
        </td>
        <td className="ob-dtable__actions">{menu}</td>
      </tr>
      {note && (
        <tr className="ob-dtable__sub-row">
          <td colSpan={SOURCE_COLUMNS}>
            <div className="ob-dtable__sub ob-source__note"><Icon name="info" size="sm" /><span className="ob-source__note-text">{note}</span></div>
          </td>
        </tr>
      )}
    </>
  );
}

/** the Source cell's name (a repository's address in its place); with an `href` (the founder, 2026-10-16) a link to the site in a new tab that
 *  cannot reach back into the app (noopener) nor learn its address (noreferrer) */
function SourceName({ row }: { row: SourceRowData }) {
  const text = row.address ?? row.name;
  const title = row.address ? row.location : row.name;   // the whole name on hover when the cell cuts it (2026-10-17)
  if (!row.href) return <span className="ob-dtable__name" title={title}>{text}</span>;
  return <a className="ob-dtable__name ob-source__link" href={row.href} target="_blank" rel="noopener noreferrer" title={title}>{text}</a>;
}

/** a new website while OmnaBase analyses it: the word as text, the kit's spinner beside it hidden from screen readers (the busy buttons' way) */
const Analysing = ({ label }: { label: string }) => <span className="ob-source__analysing"><Spinner decorative />{label}</span>;

/** a cell's words with the kit's tooltip (2026-10-16): focusable for the keyboard, and the same words reach a screen reader as the element's
 *  description, so the visible words stay as they are; without a text, the words alone */
function Tip({ text, children }: { text?: string; children: ReactNode }) {
  if (!text) return <>{children}</>;
  return <span className="ob-source__tip" data-tip={text} tabIndex={0} aria-description={text}>{children}</span>;
}

/** the State cell's alert icon and its popover: why the last sync failed, and what fixes it */
function WhyFailed({ name, error, onRetry, actions }: { name: string; error: string; onRetry?: () => void; actions?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={button} type="button" className="ob-source__why" aria-label="Why it failed" aria-expanded={open} onClick={() => setOpen((o) => !o)}><Icon name="error" size="sm" /></button>
      {open && (
        <Popover label={`Why ${name} failed`} anchor={button} onClose={() => setOpen(false)}>
          <p className="ob-popover__text">{error}</p>
          {(actions || onRetry) && <div className="ob-popover__actions" onClick={() => setOpen(false)}>{actions}{onRetry && <Button size="sm" onClick={onRetry}>Retry sync</Button>}</div>}
        </Popover>
      )}
    </>
  );
}
